import React from "react";
import {
    Box,
    Stack,
    Avatar,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    Paper,
    Chip,
    Button,
    Collapse,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import {
    useGetProjectTrackerStaff,
    useMyAccount,
} from "services/api/task-management/trackerHooks";
import { filterVisibleProjectTrackerStaff } from "../helpers/hiddenStaff";

function ManagerItem({ staff, onRemove, showRemove, loggedInStaffId, protectedStaffIds = [] }) {
    const imgUrl =
        staff?.staff_profile_image?.profile_image_url ??
        staff?.profile_image_url ??
        null;

    const isYou =
        loggedInStaffId != null && String(staff?.id) === String(loggedInStaffId);

    const displayName = isYou ? "You" : staff?.name ?? "-";
    const isProtected = protectedStaffIds.some((id) => String(id) === String(staff?.id));
    const canRemove = showRemove && !isProtected;

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1.2}
            sx={{ position: "relative", pr: canRemove ? 2.4 : 0, maxWidth: "100%" }}
        >
            <Avatar
                src={imgUrl || undefined}
                alt={displayName}
                sx={{
                    width: 21,
                    height: 21,
                    bgcolor: "#F5C542",
                    color: "black",
                    "& svg": { fontSize: 16 },
                }}
            >
                {!imgUrl ? <PersonIcon /> : null}
            </Avatar>

            <Typography
                variant="h4"
                sx={{
                    fontWeight: 600,
                    color: "#2b2b2b",
                    maxWidth: "min(520px, 60vw)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
                title={isYou ? staff?.name ?? "You" : displayName}
            >
                {displayName}
            </Typography>

            {canRemove ? (
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove?.(staff);
                    }}
                    sx={{
                        position: "absolute",
                        top: -10,
                        right: -10,
                        width: 22,
                        height: 22,
                        bgcolor: "white",
                        border: "1px solid rgba(0,0,0,0.18)",
                        "&:hover": { bgcolor: "#f3f4f6" },
                    }}
                    aria-label="Remove manager"
                >
                    <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
            ) : null}
        </Stack>
    );
}

function flattenGroupedStaff(staffResponse) {
    const grouped =
        staffResponse?.data?.data?.staff ??
        staffResponse?.data?.staff ??
        staffResponse?.staff ??
        null;

    if (Array.isArray(grouped)) return grouped;

    if (grouped && typeof grouped === "object") {
        const arr = Object.values(grouped).flat().filter(Boolean);

        const map = new Map();
        for (const s of arr) {
            if (s?.id == null) continue;
            map.set(String(s.id), s);
        }
        return Array.from(map.values());
    }

    return [];
}

function includesQuery(value, q) {
    if (!value) return false;
    return String(value).toLowerCase().includes(q);
}

export default function AddManager({
    value = [],
    onChange,
    disabled = false,
    minSearchChars = 1,
    autoAddSelf = true,
    protectedStaffIds = [],
}) {
    const [pickerOpen, setPickerOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const inputRef = React.useRef(null);
    const { data: myAccountData } = useMyAccount({ enabled: true });
    const loggedInStaff = React.useMemo(
        () => myAccountData?.data?.staff ?? null,
        [myAccountData]
    );

    const selectedIds = React.useMemo(
        () => new Set(value.map((m) => String(m.id))),
        [value]
    );

    const didAutoAddSelfRef = React.useRef(false);
    React.useEffect(() => {
        if (!autoAddSelf) return;
        if (!loggedInStaff?.id) return;
        if (didAutoAddSelfRef.current) return;

        const exists = selectedIds.has(String(loggedInStaff.id));
        if (!exists) onChange?.([loggedInStaff, ...value]);

        didAutoAddSelfRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoAddSelf, loggedInStaff?.id]);

    const { data: staffResponse, isLoading } = useGetProjectTrackerStaff(
        { limit: 9999 },
        { enabled: !disabled && pickerOpen }
    );

    const allStaff = React.useMemo(
        () =>
            filterVisibleProjectTrackerStaff(
                flattenGroupedStaff(staffResponse).filter((staff) => Number(staff?.employment_status) === 1)
            ),
        [staffResponse]
    );

    const q = query.trim().toLowerCase();
    const canFilter = !disabled && pickerOpen && q.length >= minSearchChars;

    const filteredStaff = React.useMemo(() => {
        if (!canFilter) return [];

        const result = allStaff.filter((s) => {
            return (
                includesQuery(s?.name, q) ||
                includesQuery(s?.work_email, q) ||
                includesQuery(s?.designation, q) ||
                includesQuery(s?.department, q)
            );
        });

        result.sort((a, b) =>
            String(a?.name ?? "").localeCompare(String(b?.name ?? ""))
        );

        return result.slice(0, 10);
    }, [allStaff, canFilter, q]);

    function addManager(staff) {
        if (!staff?.id) return;
        if (selectedIds.has(String(staff.id))) return;

        onChange?.([...value, staff]);
        setQuery("");
        requestAnimationFrame(() => inputRef.current?.focus());
    }

    function removeManager(staff) {
        if (!staff?.id) return;
        if (protectedStaffIds.some((id) => String(id) === String(staff.id))) return;

        onChange?.(value.filter((m) => String(m.id) !== String(staff.id)));
    }

    return (
        <Box>
            <Typography variant="body1" sx={{ fontWeight: 400, mb: 1 }}>
                Project Manager
            </Typography>

            <Box
                sx={{
                    minWidth: 300,
                    width: "fit-content",
                    maxWidth: "100%",
                    border: "1px solid rgba(0,0,0,0.3)",
                    borderRadius: 1,
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "white",
                }}
            >
                {value.length === 0 ? (
                    <Typography sx={{ color: "rgba(0,0,0,0.45)", fontWeight: 600 }}>
                        No manager selected
                    </Typography>
                ) : (
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{ flexWrap: "wrap", gap: 2, rowGap: 1.5 }}
                    >
                        {value.map((staff) => (
                            <ManagerItem
                                key={staff.id}
                                staff={staff}
                                onRemove={disabled ? undefined : removeManager}
                                showRemove={!disabled}
                                loggedInStaffId={loggedInStaff?.id}
                                protectedStaffIds={protectedStaffIds}
                            />
                        ))}
                    </Stack>
                )}
            </Box>

            <Button
                disabled={disabled}
                startIcon={<AddIcon />}
                onClick={() => {
                    setPickerOpen((v) => !v);
                    requestAnimationFrame(() => inputRef.current?.focus());
                }}
                sx={{
                    mt: 1.2,
                    textTransform: "none",
                    fontWeight: 800,
                    px: 0,
                    "&:hover": { bgcolor: "transparent" },
                }}
                variant="text"
            >
                Add project manager
            </Button>

            <Collapse in={pickerOpen} timeout={160} unmountOnExit>
                <Box sx={{ mt: 1 }}>
                    <TextField
                        fullWidth
                        inputRef={inputRef}
                        disabled={disabled}
                        placeholder="Search staff name"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {canFilter ? (
                        <Paper
                            elevation={0}
                            sx={{
                                mt: 1,
                                border: "1px solid rgba(0,0,0,0.18)",
                                borderRadius: 2,
                                overflow: "hidden",
                            }}
                        >
                            {isLoading ? (
                                <Box sx={{ px: 2, py: 1.5 }}>
                                    <Typography sx={{ color: "rgba(0,0,0,0.55)", fontWeight: 600 }}>
                                        Loading staff...
                                    </Typography>
                                </Box>
                            ) : filteredStaff.length === 0 ? (
                                <Box sx={{ px: 2, py: 1.5 }}>
                                    <Typography sx={{ color: "rgba(0,0,0,0.55)", fontWeight: 600 }}>
                                        No results found
                                    </Typography>
                                </Box>
                            ) : (
                                filteredStaff.map((staff, idx) => {
                                    const already = selectedIds.has(String(staff.id));
                                    return (
                                        <Box
                                            key={staff.id ?? idx}
                                            onClick={() => !already && addManager(staff)}
                                            sx={{
                                                px: 2,
                                                py: 1.25,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                cursor: already ? "not-allowed" : "pointer",
                                                borderTop: idx === 0 ? "none" : "1px solid rgba(0,0,0,0.08)",
                                                opacity: already ? 0.55 : 1,
                                                bgcolor: "white",
                                            }}
                                        >
                                            <Stack direction="row" spacing={1.2} alignItems="center">
                                                <Avatar
                                                    src={
                                                        staff?.staff_profile_image?.profile_image_url ??
                                                        staff?.profile_image_url ??
                                                        undefined
                                                    }
                                                    sx={{ width: 26, height: 26, bgcolor: "#F5C542", color: "black" }}
                                                >
                                                    <PersonIcon sx={{ fontSize: 16 }} />
                                                </Avatar>

                                                <Box>
                                                    <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                                        {staff?.name ?? "-"}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 12,
                                                            color: "rgba(0,0,0,0.55)",
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {(staff?.department ?? "-") + " • " + (staff?.designation ?? "-")}
                                                    </Typography>
                                                </Box>
                                            </Stack>

                                            {already ? (
                                                <Chip
                                                    label="Added"
                                                    sx={{
                                                        borderRadius: 999,
                                                        bgcolor: "#f3f4f6",
                                                        border: "1px solid rgba(0,0,0,0.12)",
                                                        fontWeight: 800,
                                                    }}
                                                />
                                            ) : null}
                                        </Box>
                                    );
                                })
                            )}
                        </Paper>
                    ) : null}
                </Box>
            </Collapse>
        </Box>
    );
}
